/**
 * AdminPerfRefreshSkippedPanel — Aug 2026.
 *
 * Companion diagnostic to the nightly `perf_refresh` job. Shows every
 * stock_cache ticker that the last refresh could NOT match against
 * Massive's grouped daily aggs (~500 tickers on a typical run), with
 * a best-guess reason bucket next to each one. Lets the admin eyeball
 * whether any important row is silently missing perf data without
 * ever touching a URL bar or curl.
 *
 * Data source: `GET /api/admin/perf-refresh/skipped` (admin-only).
 */
import { useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, X, TrendingDown, Search, FilterX, Trash2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASON_LABEL = {
  otc: 'OTC (excluded on purpose)',
  otc_foreign: 'Foreign ordinary (F-suffix OTC)',
  liquidated: 'Liquidated (no longer trades)',
  structural_warrant: 'Warrant',
  structural_unit: 'SPAC unit',
  structural_right: 'Right',
  structural_preferred: 'Preferred share',
  class_share_or_variant: 'Class share / symbol variant',
  spac: 'SPAC (blank-check shell)',
  adr: 'ADR (foreign depositary)',
  etf: 'ETF',
  cef: 'CEF (closed-end fund)',
  mutual_fund: 'Mutual fund',
  unclassified: 'Other (new IPO, halted, etc.)',
};

const REASON_TONE = {
  otc: 'text-amber-700 dark:text-amber-300',
  otc_foreign: 'text-amber-700 dark:text-amber-300',
  liquidated: 'text-rose-700 dark:text-rose-300',
  structural_warrant: 'text-sky-700 dark:text-sky-300',
  structural_unit: 'text-sky-700 dark:text-sky-300',
  structural_right: 'text-sky-700 dark:text-sky-300',
  structural_preferred: 'text-sky-700 dark:text-sky-300',
  class_share_or_variant: 'text-violet-700 dark:text-violet-300',
  spac: 'text-slate-600 dark:text-slate-300',
  adr: 'text-pink-700 dark:text-pink-300',
  etf: 'text-emerald-700 dark:text-emerald-300',
  cef: 'text-teal-700 dark:text-teal-300',
  mutual_fund: 'text-indigo-700 dark:text-indigo-300',
  unclassified: 'text-muted-foreground',
};

export default function AdminPerfRefreshSkippedPanel({ onStockClick }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [bucketFilter, setBucketFilter] = useState(null);
  const [busyTicker, setBusyTicker] = useState(null);

  // Sep 2026 — same action as the Dividend-gate audit trash can: drop the
  // ticker from stock_cache + every dependent collection (dividend or
  // non-dividend, wherever it lives) and add it to the permanent blocklist.
  const blocklistTicker = async (row, e) => {
    e?.stopPropagation?.();
    const t = row.ticker;
    setBusyTicker(t);
    try {
      const { data: resp } = await axios.post(
        `${API}/admin/blocklist/${encodeURIComponent(t)}`,
        { reason: 'perf_refresh_skipped' },
        { withCredentials: true },
      );
      toast.success(`Blocklisted ${t}`, {
        description: `Removed from ${Object.keys(resp.removed || {}).length} collections. Blocklist size: ${resp.blocklist_size}.`,
        duration: 4000,
      });
      setData((prev) => {
        if (!prev) return prev;
        const tickers = (prev.tickers || []).filter((r) => r.ticker !== t);
        const bucket_counts = { ...(prev.bucket_counts || {}) };
        if (row.reason && bucket_counts[row.reason] != null) bucket_counts[row.reason] = Math.max(0, bucket_counts[row.reason] - 1);
        return { ...prev, tickers, bucket_counts, count: Math.max(0, (prev.count ?? tickers.length + 1) - 1) };
      });
    } catch (err) {
      toast.error(`Failed to blocklist ${t}`, {
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        duration: 5000,
      });
    } finally {
      setBusyTicker(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await axios.get(
        `${API}/admin/perf-refresh/skipped`,
        { withCredentials: true },
      );
      setData(resp);
    } catch (err) {
      toast.error('Could not load skipped-tickers list', {
        description: err?.response?.data?.detail || err?.message,
      });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const openModal = () => { setOpen(true); load(); };
  const closeModal = () => setOpen(false);

  const filtered = useMemo(() => {
    const rows = data?.tickers || [];
    let out = rows;
    if (bucketFilter) {
      out = out.filter((r) => r.reason === bucketFilter);
    }
    if (query.trim()) {
      const q = query.trim().toUpperCase();
      out = out.filter((r) =>
        (r.ticker || '').includes(q) ||
        (r.name || '').toUpperCase().includes(q) ||
        (r.reason || '').toUpperCase().includes(q),
      );
    }
    return out;
  }, [data, query, bucketFilter]);

  const clearFilters = () => { setBucketFilter(null); setQuery(''); };
  const clearList = () => { setData(null); setBucketFilter(null); setQuery(''); };

  const openTicker = (ticker, e) => {
    if (e) e.stopPropagation();
    if (typeof onStockClick === 'function') {
      onStockClick(ticker);
    } else {
      window.dispatchEvent(new CustomEvent('open-stock-modal', {
        detail: { ticker: String(ticker).toUpperCase() },
      }));
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-slate-600 hover:bg-slate-700 text-white transition-colors"
        data-testid="admin-perf-refresh-skipped-btn"
        title="See which stock_cache tickers the last nightly perf refresh could NOT match against Massive's grouped daily aggs."
      >
        <TrendingDown className="w-3 h-3" />
        Perf Refresh Skipped
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
          onClick={closeModal}
          data-testid="admin-perf-refresh-skipped-modal"
        >
          <div
            className="bg-background border border-border/80 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-border/60 flex items-center gap-3">
              <TrendingDown className="w-4 h-4 text-slate-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold">Perf Refresh — Skipped Tickers</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  Rows whose <code>perf_1m/6m/1y</code> could not be updated because Massive&apos;s grouped daily aggs had no current price for the ticker.
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="text-muted-foreground hover:text-foreground p-1 rounded"
                data-testid="admin-perf-refresh-skipped-close"
                aria-label="Close skipped tickers modal"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 space-y-3">
              {loading && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading…
                </div>
              )}

              {!loading && data && (
                <>
                  <div className="text-[11px] text-muted-foreground">
                    <b>{data.count}</b> skipped as of{' '}
                    {data.last_perf_refresh_at
                      ? new Date(data.last_perf_refresh_at).toLocaleString(undefined, {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })
                      : 'never run'}
                    .
                  </div>

                  {data.bucket_counts && Object.keys(data.bucket_counts).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center" data-testid="admin-perf-refresh-buckets">
                      {Object.entries(data.bucket_counts)
                        .sort((a, b) => b[1] - a[1])
                        .map(([key, count]) => {
                          const isActive = bucketFilter === key;
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => setBucketFilter(isActive ? null : key)}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-mono border transition-colors cursor-pointer ${
                                isActive
                                  ? 'border-violet-500 bg-violet-500/20 ring-1 ring-violet-500/40 text-foreground'
                                  : `border-border/60 bg-muted/40 hover:bg-muted/70 ${REASON_TONE[key] || ''}`
                              }`}
                              title={isActive ? `Clear ${REASON_LABEL[key] || key} filter` : `Show only ${REASON_LABEL[key] || key}`}
                              data-testid={`admin-perf-refresh-bucket-${key}`}
                              aria-pressed={isActive}
                            >
                              <b>{count}</b> {REASON_LABEL[key] || key}
                            </button>
                          );
                        })}
                      {(bucketFilter || query) && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10.5px] font-semibold text-violet-600 dark:text-violet-300 hover:bg-violet-500/10 border border-transparent"
                          title="Clear the active filter and search"
                          data-testid="admin-perf-refresh-clear-filters"
                        >
                          <FilterX className="w-3 h-3" />
                          Clear filters
                        </button>
                      )}
                    </div>
                  )}

                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Filter by ticker, name, or reason…"
                      className="w-full pl-7 pr-2 py-1.5 rounded-md border border-border/60 bg-background text-xs focus:outline-none focus:ring-2 focus:ring-violet-500/40"
                      data-testid="admin-perf-refresh-search"
                    />
                  </div>

                  <ul className="space-y-1" data-testid="admin-perf-refresh-skipped-list">
                    {filtered.slice(0, 500).map((r) => (
                      <li
                        key={r.ticker}
                        className="flex items-center justify-between gap-3 px-2.5 py-1.5 rounded border border-border/40 bg-muted/10 text-xs"
                        data-testid={`admin-perf-refresh-row-${r.ticker}`}
                      >
                        <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={(e) => blocklistTicker(r, e)}
                            disabled={busyTicker === r.ticker}
                            className="shrink-0 p-1 rounded text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10 dark:hover:text-rose-400 transition-colors disabled:opacity-50"
                            title={`Permanently remove ${r.ticker} from the database and add to the blocklist (never re-add).`}
                            aria-label={`Blocklist ${r.ticker}`}
                            data-testid={`admin-perf-refresh-blocklist-${r.ticker}`}
                          >
                            {busyTicker === r.ticker
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => openTicker(r.ticker, e)}
                            className="font-mono font-bold text-sm text-slate-700 dark:text-slate-200 hover:text-violet-600 hover:underline"
                            title={`Open ${r.ticker} in the stock modal`}
                            data-testid={`admin-perf-refresh-ticker-${r.ticker}`}
                          >
                            {r.ticker}
                          </button>
                          <span className="text-[10.5px] text-muted-foreground truncate">
                            {r.name || '—'}
                          </span>
                        </div>
                        <span
                          className={`shrink-0 font-mono text-[10px] uppercase tracking-wide ${REASON_TONE[r.reason] || ''}`}
                        >
                          {r.reason.replace('structural_', '')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {filtered.length > 500 && (
                    <div className="text-[10.5px] text-muted-foreground italic text-center py-1">
                      Showing first 500 of {filtered.length}. Narrow with the filter.
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t border-border/60 text-[10px] text-muted-foreground flex items-center justify-between gap-3">
              <span>
                {data
                  ? bucketFilter || query
                    ? `${filtered.length} of ${data.count} shown`
                    : `${data.count} skipped`
                  : ''}
              </span>
              <div className="flex items-center gap-3">
                {data && (
                  <button
                    type="button"
                    onClick={clearList}
                    disabled={loading}
                    className="inline-flex items-center gap-1 text-rose-500 hover:text-rose-400 disabled:opacity-50"
                    title="Clear the list from the UI. It will repopulate the next time you click Refresh or when the nightly cron runs."
                    data-testid="admin-perf-refresh-clear-list"
                  >
                    <Trash2 className="w-3 h-3" />
                    Clear list
                  </button>
                )}
                <button
                  type="button"
                  onClick={load}
                  disabled={loading}
                  className="inline-flex items-center gap-1 text-violet-500 hover:text-violet-400 disabled:opacity-50"
                  data-testid="admin-perf-refresh-refresh"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
